import { QueryHookOptions, useQuery } from '@apollo/client';
import { DocumentNode } from 'graphql';
import { flatten, isNumber } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { GeneralFunction, IPagination, IQueryWithPagination } from 'types/common';
import { getGraphQLQueryName } from 'utils';

export interface IReactiveQuerySelectProps {
  graphQLQuery: DocumentNode;
  optionMapper?: GeneralFunction;
  queryProps?: QueryHookOptions;
}

export type WithKey<K extends string | number | symbol, ValueType> = {
  [k in K]: ValueType;
};

export interface IUseInfiniteDataQueryReturnValues<T = any> {
  loading?: boolean;
  firstLoading?: boolean;
  refetch: GeneralFunction;
  fetchMore: GeneralFunction;
  fetchNextPageData: GeneralFunction;
  meta?: IPagination;
  data: T[];
  options: any[];
  fetchByQuery: GeneralFunction;
}

export default function useInfiniteDataQuery<T = any>({
  graphQLQuery,
  optionMapper,
  queryProps = {},
}: IReactiveQuerySelectProps) {
  const queryName = getGraphQLQueryName(graphQLQuery) || '';

  const [dataState, setDataState] = useState<T[]>([]);
  const [metaState, setMetaState] = useState<IPagination>();

  const [temporalDataState, setTemporalDataState] = useState<T[]>([]);
  const [temporalMetaState, setTemporalMetaState] = useState<IPagination>();

  const [firstLoading, setFirstLoading] = useState<boolean | undefined>(undefined);

  const { loading, data: queryResult, fetchMore, ...useQueryProps } = useQuery<
    WithKey<typeof queryName, IQueryWithPagination<T[]>>
  >(graphQLQuery, {
    fetchPolicy: 'cache-and-network',
    // nextFetchPolicy: 'cache-first',
    ...queryProps,
    notifyOnNetworkStatusChange: true,
    variables: {
      ...queryProps?.variables,
      pagination: {
        page: 1,
        ...(queryProps?.variables?.pagination || {}),
      },
    },
  });

  const { data, meta } = useMemo<Partial<{ data: T; meta: IPagination }>>(() => queryResult?.[queryName] || {}, [
    queryResult,
  ]);

  useEffect(() => {
    if (loading && firstLoading === undefined) {
      setFirstLoading(true);
    } else if (!loading) {
      setFirstLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    // only get the data of first page using useEffect
    if (data && dataState.length === 0) {
      setDataState((c) => {
        const current = [...c];
        current[0] = data;
        return current;
      });
      setMetaState(meta);
    }
  }, [data, meta]);

  const fetchNextPageData = (pageParam?: number) => {
    let page = pageParam;
    if (!isNumber(pageParam)) page = 0;
    if ((!loading && metaState?.hasNextPage) || !!page) {
      fetchMore({ variables: { pagination: { page: (page || metaState?.page || 0) + 1 } } })
        .then((result: Record<string, any>) => {
          const { data: newResultData, meta: newResultMeta } = result?.data?.[queryName] || {};
          if (temporalDataState.length === 0) {
            setDataState((c) => {
              const current = [...c];
              current[(newResultMeta?.page || 1) - 1] = newResultData;
              return current;
            });
            setMetaState(newResultMeta);
          } else {
            setTemporalDataState((c) => {
              const current = [...c];
              current[(newResultMeta?.page || 1) - 1] = newResultData;
              return current;
            });
            setTemporalMetaState(newResultMeta);
          }
        })
        .catch((err) => {
          console.log('Err', err);
        });
    }
  };

  const refetch = () => {
    fetchNextPageData(1);
  };

  const fetchByQuery = (v?: string) => {
    fetchMore({ variables: { pagination: { page: temporalMetaState?.page || 1 }, query: v } })
      .then((result: Record<string, any>) => {
        const { data: newResultData, meta: newResultMeta } = result?.data?.[queryName] || {};
        setTemporalDataState((c) => {
          const current = [...c];
          if (v) {
            current[(newResultMeta?.page || 1) - 1] = newResultData;
            return current;
          }
          return [];
        });
        setTemporalMetaState(() => {
          if (v) return newResultMeta;
          return undefined;
        });
      })
      .catch((err) => {
        console.log('Err', err);
      });
  };

  const flattenedData = useMemo(() => {
    if (temporalDataState.length > 0) {
      return flatten(temporalDataState);
    }
    return dataState ? flatten(dataState) : [];
  }, [dataState, temporalDataState]);

  const options = useMemo(() => (optionMapper ? flattenedData?.map(optionMapper) || [] : []), [
    flattenedData,
    optionMapper,
  ]);

  return {
    ...useQueryProps,
    refetch,
    loading,
    fetchMore,
    fetchNextPageData,
    meta: temporalMetaState || metaState,
    data: flattenedData,
    options,
    fetchByQuery,
    firstLoading,
  };
}
